import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import './AdminDashboard.css';
import { useLocation, useNavigate } from 'react-router-dom';

const formatNumber = (value) => {
  if (value === null || value === undefined) return '—';
  return Number(value).toLocaleString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const formatPercent = (used, limit) => {
  if (used === null || used === undefined || !limit) return '-';
  const pct = Math.min(Math.max((used / limit) * 100, 0), 100);
  return `${pct.toFixed(1)}%`;
};

const formatPercentValue = (value) => {
  if (value === null || value === undefined) return '-';
  return `${value.toFixed(1)}%`;
};

const formatUsageLine = (used, limit, showUsage = true) => {
  if (!limit) return '—';
  if (!showUsage || used === null || used === undefined) {
    return `— / ${formatNumber(limit)}`;
  }
  return `${formatNumber(used)} / ${formatNumber(limit)}`;
};

const getUsageLevel = (used, limit) => {
  if (used === null || used === undefined || !limit) return 'low';
  const pct = (used / limit) * 100;
  if (pct >= 80) return 'high';
  if (pct >= 50) return 'medium';
  return 'low';
};

const readStoredToken = () => localStorage.getItem('adminToken') || '';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenInput, setTokenInput] = useState(() => readStoredToken());
  const [activeToken, setActiveToken] = useState(() => readStoredToken());
  const [initializing, setInitializing] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [queueExpanded, setQueueExpanded] = useState(false);
  const [weatherEmail, setWeatherEmail] = useState('');
  const [weatherLimit, setWeatherLimit] = useState('');
  const [weatherSubmitting, setWeatherSubmitting] = useState(false);
  const [weatherResult, setWeatherResult] = useState(null);
  const [containerJobs, setContainerJobs] = useState(null);
  const [containerJobsLoading, setContainerJobsLoading] = useState(false);
  const [expandedJobs, setExpandedJobs] = useState([]);

  const fetchMetrics = useCallback(
    async (currentToken) => {
      if (!currentToken) {
        setError('Provide the admin token to load metrics.');
        setMetrics(null);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(`${API_BASE_URL}/admin/metrics`, {
          headers: { 'X-Admin-Token': currentToken }
        });
        setMetrics(response.data);
      } catch (err) {
        const message =
          err.response?.data?.error || err.message || 'Failed to load metrics';
        setError(message);
        setMetrics(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const fetchContainerJobs = useCallback(
    async (currentToken) => {
      if (!currentToken) return;

      setContainerJobsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/admin/container-jobs/status`, {
          headers: { 'X-Admin-Token': currentToken }
        });
        setContainerJobs(response.data);
      } catch (err) {
        // Silently fail - container jobs may not be available in all environments
        console.warn('Failed to fetch container jobs status:', err);
        setContainerJobs(null);
      } finally {
        setContainerJobsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlToken = params.get('token');

    if (urlToken) {
      localStorage.setItem('adminToken', urlToken);
      setTokenInput(urlToken);
      setActiveToken(urlToken);
      params.delete('token');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : ''
        },
        { replace: true }
      );
      setInitializing(false);
      return;
    }

    if (!readStoredToken()) {
      navigate('/', { replace: true });
      return;
    }

    setInitializing(false);
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (activeToken && !initializing) {
      fetchMetrics(activeToken);
      fetchContainerJobs(activeToken);
    }
  }, [activeToken, fetchMetrics, fetchContainerJobs, initializing]);

  const handleApplyToken = (event) => {
    event.preventDefault();
    const trimmed = tokenInput.trim();
    localStorage.setItem('adminToken', trimmed);
    setActiveToken(trimmed);
    if (!trimmed) {
      setMetrics(null);
    }
  };

  const handleRefresh = () => {
    if (activeToken) {
      fetchMetrics(activeToken);
      fetchContainerJobs(activeToken);
    }
  };

  const fetchUsers = useCallback(async () => {
    if (!activeToken) {
      setUsersError('Provide the admin token to view users.');
      setUsersData([]);
      return;
    }

    setUsersLoading(true);
    setUsersError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: { 'X-Admin-Token': activeToken }
      });
      setUsersData(response.data.users || []);
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || 'Failed to load users';
      setUsersError(message);
      setUsersData([]);
    } finally {
      setUsersLoading(false);
    }
  }, [activeToken]);

  useEffect(() => {
    if (selectedPanel === 'users') {
      fetchUsers();
    }
  }, [selectedPanel, fetchUsers]);

  const togglePanel = (panel) => {
    setSelectedPanel((prev) => (prev === panel ? null : panel));
  };

  const toggleJob = (jobId) => {
    setExpandedJobs((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const handleCardKeyDown = (panel) => (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      togglePanel(panel);
    }
  };

  const stravaStatus = metrics?.strava;
  const stravaLimitsAvailable =
    stravaStatus &&
    stravaStatus.status !== 'unknown' &&
    stravaStatus.short_limit !== undefined &&
    stravaStatus.long_limit !== undefined;
  const shortUsageAvailable =
    typeof stravaStatus?.short_used === 'number' &&
    Number.isFinite(stravaStatus.short_used);
  const longUsageAvailable =
    typeof stravaStatus?.long_used === 'number' &&
    Number.isFinite(stravaStatus.long_used);

  const jobMeta = [
    {
      id: 'strava-sync',
      title: 'Strava Sync (webhook + manual backfill)',
      summary: 'Primary ingestion path; keeps activities current and backfills missed webhooks.',
      details: [
        'Triggers: Strava webhooks; POST /api/strava/sync/backfill for manual/backfill runs.',
        'What it does: syncs recent activities, reverse geocodes, and queues weather backfill when gaps remain.',
        'Monitor: Admin queue panel for running/failed jobs; container logs for sync_recent_activities.',
      ],
      status: metrics?.queue?.running_jobs?.some((job) => job.name === 'sync_recent_activities')
        ? 'running'
        : metrics?.queue?.recent?.some((job) => job.name === 'sync_recent_activities')
        ? 'recent'
        : 'idle',
    },
    {
      id: 'location-backfill',
      title: 'Location Backfill (reverse geocoding)',
      summary: 'Populates location_city/state/country for legacy activities missing location.',
      details: [
        'Triggers: flask backfill-locations (per user or bulk); scripts/post_deploy.py queues per-user jobs.',
        'What it does: replays Strava history via sync_recent_activities(days=5475).',
        'Monitor: search logs for "backfill-locations" or "location backfill"; queue panel for sync_recent_activities jobs.',
      ],
      status: 'unknown',
    },
    {
      id: 'weather-backfill',
      title: 'Weather Backfill (background)',
      summary: 'Fills weather metrics for activities with GPS but missing weather.',
      details: [
        'Triggers: auto after Strava sync; POST /api/weather/backfill; POST /api/admin/weather/sync (queues job).',
        'What it does: batches weather_service.backfill_weather_for_user; enqueues a full run when gaps remain.',
        `Last run: ${metrics?.weather?.last_backfill ? formatDateTime(metrics.weather.last_backfill.status_time || metrics.weather.last_backfill.startTime || metrics.weather.last_backfill.updated_at) : 'not recorded'}.`,
        'Monitor: logs for "Weather backfill"; container jobs status if deployed as Container Apps job.',
      ],
      status:
        containerJobs?.weather_backfill_job?.status ||
        metrics?.weather?.last_backfill?.status ||
        'unknown',
    },
    {
      id: 'cleanup-job',
      title: 'Cleanup Job (Container Apps scheduled)',
      summary: 'Housekeeping tasks (logs/temp) run on a schedule via Container Apps job.',
      details: [
        'Triggers: scheduled in Azure Container Apps job (see backend/scripts/deploy_cleanup_job.sh).',
        'Monitor: container jobs status endpoint; Azure portal execution history.',
      ],
      status: containerJobs?.cleanup_job?.status || 'unknown',
    },
  ];
  const stravaHasLiveUsage = shortUsageAvailable || longUsageAvailable;
  const stravaRows = stravaLimitsAvailable
    ? [
        {
          label: '15-minute window',
          limit: stravaStatus.short_limit,
          used: stravaStatus.short_used,
          remaining: stravaStatus.short_remaining,
        },
        {
          label: 'Daily window',
          limit: stravaStatus.long_limit,
          used: stravaStatus.long_used,
          remaining: stravaStatus.long_remaining,
        },
      ]
    : [];

  if (initializing) {
    return null;
  }

  return (
    <div className="admin-dashboard">
      <section className="admin-card admin-header">
        <div>
          <h2>Admin Insights</h2>
          <p className="admin-subtext">
            View platform health and Strava API capacity. Provide the shared admin token to unlock data.
          </p>
        </div>
        <form className="admin-token-form" onSubmit={handleApplyToken}>
          <input
            type="password"
            placeholder="Admin token"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
          />
          <button type="submit">Apply</button>
          <button type="button" onClick={handleRefresh} disabled={loading || !activeToken}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </form>
      </section>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-grid">
        <div
          className={`admin-card selectable ${queueExpanded ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => setQueueExpanded((prev) => !prev)}
          onKeyDown={handleCardKeyDown('queue')}
        >
          <h3>Background Queue</h3>
          <p className="admin-metric">{formatNumber(metrics?.queue?.queued)} queued</p>
          <p className="admin-subtext">
            Running: {formatNumber(metrics?.queue?.running)} • Failed: {formatNumber(metrics?.queue?.failed)}
          </p>
          <p className="admin-hint">Click for recent job history.</p>
        </div>
        <div
          className={`admin-card selectable ${selectedPanel === 'users' ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => togglePanel('users')}
          onKeyDown={handleCardKeyDown('users')}
        >
          <h3>Total Users</h3>
          <p className="admin-metric">{formatNumber(metrics?.users?.total)}</p>
          <p className="admin-subtext">
            Strava connected: {formatNumber(metrics?.users?.strava_connected)}
          </p>
          <p className="admin-hint">Click to view roster & verification state.</p>
        </div>
        <div className="admin-card">
          <h3>Active (30d)</h3>
          <p className="admin-metric">{formatNumber(metrics?.users?.active_last_30_days)}</p>
          <p className="admin-subtext">
            Activities (7d): {formatNumber(metrics?.users?.activities_last_7_days)}
          </p>
        </div>
        <div className="admin-card">
          <h3>Activities</h3>
          <p className="admin-metric">{formatNumber(metrics?.activities?.total)}</p>
          <p className="admin-subtext">
            Strava: {formatNumber(metrics?.activities?.strava)} • Manual: {formatNumber(metrics?.activities?.manual)}
          </p>
          <p className="admin-subtext">
            Missing weather: {formatNumber(metrics?.activities?.missing_weather)} • Missing location: {formatNumber(metrics?.activities?.missing_location)}
          </p>
          <p className="admin-hint">
            {formatNumber(metrics?.activities?.new_last_7_days)} new in last 7 days • Last run{' '}
            {formatDateTime(metrics?.activities?.last_activity_at)}
          </p>
        </div>
        <div className="admin-card">
          <h3>Weather Coverage</h3>
          <p className="admin-metric">
            {formatPercentValue(metrics?.weather?.coverage_pct)}
          </p>
          <p className="admin-subtext">
            With weather: {formatNumber(metrics?.weather?.with_weather)} /{' '}
            {formatNumber(metrics?.weather?.total_with_location)}
          </p>
          <p className="admin-subtext">
            Missing (GPS known): {formatNumber(metrics?.weather?.missing)}
          </p>
          <p className="admin-hint">
            {metrics?.weather?.last_backfill ? (
              <>
                Last backfill job {metrics.weather.last_backfill.status} ({formatNumber(metrics.weather.last_backfill.success)}/
                {formatNumber(metrics.weather.last_backfill.total)} updated)
              </>
            ) : (
              'No background weather backfill recorded yet.'
            )}
          </p>
        </div>
        <div className="admin-card">
          <h3>Container Jobs</h3>
          {containerJobsLoading ? (
            <p className="admin-subtext">Loading job status...</p>
          ) : containerJobs ? (
            <>
              {['CLI_NOT_AVAILABLE', 'SDK_NOT_AVAILABLE', 'ConfigError', 'AuthError'].includes(containerJobs.cleanup_job?.status) ||
               ['CLI_NOT_AVAILABLE', 'SDK_NOT_AVAILABLE', 'ConfigError', 'AuthError'].includes(containerJobs.weather_backfill_job?.status) ? (
                <div className="admin-subsection">
                  <p className="admin-subtext">
                    <span className="admin-tag muted">Setup Required</span>
                  </p>
                  <p className="admin-hint">
                    {containerJobs.cleanup_job?.error || containerJobs.weather_backfill_job?.error ||
                     'Azure SDK not configured. Add AZURE_SUBSCRIPTION_ID environment variable to backend.'}
                  </p>
                  <p className="admin-hint">
                    Jobs are still running on schedule - check Azure Portal for status.
                  </p>
                  <p className="admin-hint">
                    Scheduled: Cleanup (02:00 UTC daily), Weather Backfill (03:00 UTC daily, auto-continues until complete)
                  </p>
                </div>
              ) : (
                <>
                  <div className="admin-subsection">
                    <p className="admin-subtext">
                      <strong>Cleanup Job:</strong>{' '}
                      <span className={`admin-tag ${
                        containerJobs.cleanup_job?.status === 'Succeeded' ? 'success' :
                        containerJobs.cleanup_job?.status === 'Running' ? 'muted' :
                        containerJobs.cleanup_job?.status === 'Failed' ? 'danger' : 'muted'
                      }`}>
                        {containerJobs.cleanup_job?.status || 'Unknown'}
                      </span>
                    </p>
                    <p className="admin-hint">
                      Last run: {formatDateTime(containerJobs.cleanup_job?.startTime)}
                    </p>
                  </div>
                  <div className="admin-subsection">
                    <p className="admin-subtext">
                      <strong>Weather Backfill:</strong>{' '}
                      <span className={`admin-tag ${
                        containerJobs.weather_backfill_job?.status === 'Succeeded' ? 'success' :
                        containerJobs.weather_backfill_job?.status === 'Running' ? 'muted' :
                        containerJobs.weather_backfill_job?.status === 'Failed' ? 'danger' : 'muted'
                      }`}>
                        {containerJobs.weather_backfill_job?.status || 'Unknown'}
                      </span>
                    </p>
                    <p className="admin-hint">
                      Last run: {formatDateTime(containerJobs.weather_backfill_job?.startTime)}
                    </p>
                    <p className="admin-hint">
                      Processes 500 activities/run with auto-continue (completes in hours, not days)
                    </p>
                  </div>
                </>
              )}
            </>
          ) : (
            <p className="admin-subtext">Container job status unavailable</p>
          )}
        </div>
        <div className="admin-card">
          <h3>Failures (24h)</h3>
          <p className="admin-metric">{formatNumber(metrics?.failures?.count_last_24h)}</p>
          <p className="admin-subtext">
            Recent errors logged to backend/logs/error.log
          </p>
        </div>
        <div
          className={`admin-card selectable ${selectedPanel === 'strava' ? 'active' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => stravaLimitsAvailable && togglePanel('strava')}
          onKeyDown={handleCardKeyDown('strava')}
        >
          <h3>Strava API Usage</h3>
          {stravaLimitsAvailable ? (
            <>
              <div className="strava-progress-container">
                <div className="strava-usage-row">
                  <span className="strava-usage-label">15-minute window</span>
                  <span className="strava-usage-numbers">
                    {formatUsageLine(
                      stravaStatus.short_used,
                      stravaStatus.short_limit,
                      shortUsageAvailable
                    )}
                  </span>
                </div>
                {shortUsageAvailable && (
                  <div className="strava-progress-bar">
                    <div
                      className={`strava-progress-fill ${getUsageLevel(stravaStatus.short_used, stravaStatus.short_limit)}`}
                      style={{ width: formatPercent(stravaStatus.short_used, stravaStatus.short_limit) }}
                    />
                  </div>
                )}
              </div>

              <div className="strava-progress-container">
                <div className="strava-usage-row">
                  <span className="strava-usage-label">Daily window</span>
                  <span className="strava-usage-numbers">
                    {formatUsageLine(
                      stravaStatus.long_used,
                      stravaStatus.long_limit,
                      longUsageAvailable
                    )}
                  </span>
                </div>
                {longUsageAvailable && (
                  <div className="strava-progress-bar">
                    <div
                      className={`strava-progress-fill ${getUsageLevel(stravaStatus.long_used, stravaStatus.long_limit)}`}
                      style={{ width: formatPercent(stravaStatus.long_used, stravaStatus.long_limit) }}
                    />
                  </div>
                )}
              </div>

              <p className="admin-updated">
                Updated {stravaStatus.updated_at ? formatDateTime(stravaStatus.updated_at) : '—'}
              </p>
              <p className="admin-hint">
                {stravaHasLiveUsage
                  ? 'Click for detailed breakdown and remaining capacity.'
                  : 'No live usage captured yet. Limits shown for reference.'}
              </p>
            </>
          ) : (
            <p className="admin-subtext">
              Strava limits unavailable. Ensure the backend recorded rate-limit headers.
            </p>
          )}
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-header">
          <h3>Recent Failures</h3>
          <span className="admin-updated">
            Updated {metrics?.generated_at || '—'}
          </span>
        </div>
        {metrics?.failures?.recent?.length ? (
          <ul className="admin-log-list">
            {metrics.failures.recent.map((entry, index) => (
              <li key={`${entry.timestamp}-${index}`}>
                <div className="admin-log-timestamp">{entry.timestamp || 'Unknown time'}</div>
                <div className="admin-log-message">{entry.message || entry.raw}</div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="admin-subtext">No errors captured yet 🎉</p>
        )}
      </section>

      {selectedPanel === 'users' && (
        <section className="admin-card">
          <div className="admin-card-header">
            <div>
              <h3>User Directory</h3>
              <p className="admin-subtext">
                Email confirmation, Strava link, and activity totals for quick audits.
              </p>
            </div>
            <button type="button" className="admin-refresh" onClick={fetchUsers} disabled={usersLoading}>
              {usersLoading ? 'Refreshing...' : 'Refresh list'}
            </button>
          </div>
          {usersError && <div className="admin-error slim">{usersError}</div>}
          {!usersError && (
            <div className="admin-table-wrapper">
              {usersLoading ? (
                <p className="admin-subtext">Loading users…</p>
              ) : usersData.length === 0 ? (
                <p className="admin-subtext">No users found.</p>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Username</th>
                      <th>Email Confirmed</th>
                      <th>Strava</th>
                      <th>Activities</th>
                      <th>Share Setting</th>
                      <th>Created</th>
                      <th>Last Login</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersData.map((user) => (
                      <tr key={user.id}>
                        <td>{user.email}</td>
                        <td>{user.username}</td>
                        <td>
                          <span className={`admin-tag ${user.email_confirmed ? 'success' : 'muted'}`}>
                            {user.email_confirmed ? 'Confirmed' : 'Pending'}
                          </span>
                        </td>
                        <td>
                          <span className={`admin-tag ${user.strava_connected ? 'success' : 'muted'}`}>
                            {user.strava_connected ? 'Linked' : 'Missing'}
                          </span>
                        </td>
                        <td>{formatNumber(user.activity_count)}</td>
                        <td>{user.share_stats || 'friends'}</td>
                        <td>{formatDateTime(user.created_at)}</td>
                        <td>{formatDateTime(user.last_login_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </section>
      )}

      {selectedPanel === 'strava' && stravaLimitsAvailable && (
        <section className="admin-card">
          <div className="admin-card-header">
            <div>
              <h3>Strava Rate Limits</h3>
              <p className="admin-subtext">
                Derived from the most recent Strava API response headers captured by the backend.
              </p>
            </div>
            <span className="admin-updated">Updated {formatDateTime(stravaStatus.updated_at)}</span>
          </div>
          {stravaStatus.status === 'bootstrap' && (
            <p className="admin-subtext">
              No live Strava requests captured yet. These values reflect the default app limits.
            </p>
          )}
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Window</th>
                  <th>Limit</th>
                  <th>Used</th>
                  <th>Remaining</th>
                  <th>% Used</th>
                </tr>
              </thead>
              <tbody>
                {stravaRows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td>{formatNumber(row.limit)}</td>
                    <td>{formatNumber(row.used)}</td>
                    <td>{formatNumber(row.remaining)}</td>
                    <td>{formatPercent(row.used, row.limit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {queueExpanded && (
        <section className="admin-card">
          <div className="admin-card-header">
            <h3>Recent Queue Jobs</h3>
            <p className="admin-subtext">
              Monitoring {metrics?.queue?.recent?.length || 0} most recent entries.
            </p>
          </div>
          <div className="admin-subsection">
            <h4>Running Jobs</h4>
            {metrics?.queue?.running_jobs && metrics.queue.running_jobs.length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Progress</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.queue.running_jobs.map((job) => (
                    <tr key={job.id}>
                      <td>
                        <div className="mono">{job.id}</div>
                        <div className="admin-subtext">{job.name || 'background job'}</div>
                      </td>
                      <td>{job.progress}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="admin-subtext">No jobs are currently running.</p>
            )}
          </div>
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {(metrics?.queue?.recent || []).map((job) => (
                  <tr key={job.id}>
                    <td className="mono">{job.id}</td>
                    <td>
                      <span className={`admin-tag ${job.status === 'failed' ? 'danger' : job.status === 'finished' ? 'success' : 'muted'}`}>
                        {job.status}
                      </span>
                    </td>
                    <td>{job.progress}%</td>
                    <td>{job.error || '—'}</td>
                  </tr>
                ))}
                {(!metrics?.queue?.recent || metrics.queue.recent.length === 0) && (
                  <tr>
                    <td colSpan={4}>No jobs recorded yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="admin-card">
        <div className="admin-card-header">
          <div>
            <h3>Backfill & Sync Jobs</h3>
            <p className="admin-subtext">
              Click a row to view what each job does, how it is triggered, and where to monitor it.
            </p>
          </div>
        </div>
        <div className="admin-table-wrapper">
          <table className="admin-table admin-table-collapsible">
            <thead>
              <tr>
                <th>Job</th>
                <th>Status</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {jobMeta.map((job) => (
                <React.Fragment key={job.id}>
                  <tr
                    className="admin-clickable-row"
                    onClick={() => toggleJob(job.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleJob(job.id);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-expanded={expandedJobs.includes(job.id)}
                  >
                    <td>{job.title}</td>
                    <td>
                      <span
                        className={`admin-tag ${
                          job.status === 'failed'
                            ? 'danger'
                            : job.status === 'running'
                            ? 'muted'
                            : job.status === 'success' || job.status === 'recent'
                            ? 'success'
                            : 'muted'
                        }`}
                      >
                        {job.status || 'unknown'}
                      </span>
                    </td>
                    <td>{job.summary}</td>
                  </tr>
                  {expandedJobs.includes(job.id) && (
                    <tr className="admin-collapsible-row">
                      <td colSpan={3}>
                        <ul className="admin-list">
                          {job.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-header">
          <div>
            <h3>Actions</h3>
            <p className="admin-subtext">
              Weather backfill runs automatically after Strava syncs. Use this to reprocess a legacy user.
            </p>
          </div>
        </div>
        <form
          className="admin-action-form"
          onSubmit={async (event) => {
            event.preventDefault();
            if (!weatherEmail.trim()) {
              setWeatherResult({ type: 'error', message: 'Email is required' });
              return;
            }
            setWeatherSubmitting(true);
            setWeatherResult(null);
            try {
              await axios.post(
                `${API_BASE_URL}/admin/weather/sync`,
                { email: weatherEmail.trim(), limit: weatherLimit ? Number(weatherLimit) : undefined },
                { headers: { 'X-Admin-Token': activeToken } }
              );
              setWeatherResult({
                type: 'success',
                message: 'Weather sync queued successfully',
              });
              setWeatherEmail('');
              setWeatherLimit('');
            } catch (err) {
              const message =
                err.response?.data?.error || err.message || 'Failed to queue weather sync';
              setWeatherResult({ type: 'error', message });
            } finally {
              setWeatherSubmitting(false);
            }
          }}
        >
          <input
            type="email"
            placeholder="user@example.com"
            value={weatherEmail}
            onChange={(e) => setWeatherEmail(e.target.value)}
          />
          <input
            type="number"
            placeholder="Limit (optional)"
            value={weatherLimit}
            onChange={(e) => setWeatherLimit(e.target.value)}
          />
          <button type="submit" disabled={weatherSubmitting}>
            {weatherSubmitting ? 'Queuing...' : 'Queue weather sync'}
          </button>
        </form>
        {weatherResult && (
          <div className={`admin-action-result ${weatherResult.type}`}>
            {weatherResult.message}
          </div>
        )}
      </section>

      <section className="admin-card">
        <div className="admin-card-header">
          <h3>Next Steps</h3>
        </div>
        <ul className="admin-next-steps">
          <li>Surface queue depth and job history from the background worker.</li>
          <li>Layer role-based auth so admin routes stay separate from consumer login.</li>
        </ul>
      </section>
    </div>
  );
};

export default AdminDashboard;
